class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form.search');
    this.results = this.querySelector('[data-predictive-search]');
    this.defaultState = this.querySelector('[data-search-default]');
    this.panel = this.querySelector('[data-search-panel]');
    this.backdrop = this.querySelector('[data-search-backdrop]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.exitButton = this.querySelector('[data-search-exit]');
    this.predictiveUrl =
      this.dataset.predictiveUrl || routes.predictive_search_url;

    this.isOpen = false;
    this.abortController = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (
      !this.input ||
      !this.form ||
      !this.results ||
      !this.defaultState ||
      !this.panel
    ) {
      return;
    }

    this.form.addEventListener(
      'submit',
      this.onFormSubmit.bind(this)
    );

    this.input.addEventListener(
      'input',
      this.debounce(() => this.onChange(), 250)
    );

    this.input.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.exitButton?.addEventListener('click', () => {
      this.close();
      this.input.blur();
    });

    this.clearButton?.addEventListener('click', () => {
      this.input.value = '';

      this.updateClearButton();

      this.results.innerHTML = '';

      this.removeAttribute('results');

      this.showDefault();
      this.open();

      this.input.focus();
    });

    const mobileMenu = document.getElementById('nav-hamburger');

if (mobileMenu) {
  mobileMenu.addEventListener('toggle', () => {
    if (mobileMenu.open && this.isOpen) {
      this.close();
      this.input.blur();
    }
  });
}

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-close]')) {
        this.close();
        this.input.blur();
      }
    });

    this.backdrop?.addEventListener('click', () => {
      this.close();
      this.input.blur();
    });

    document.addEventListener('keydown', (event) => {
      if (
        event.key === 'Escape' &&
        this.isOpen
      ) {
        this.close();
        this.input.blur();
      }
    });

    window.addEventListener(
      'resize',
      this.debounce(() => {
        if (this.isOpen) {
          this.positionLayers();
        }
      }, 100)
    );
  }

  debounce(fn, wait) {
    let timer;

    return (...args) => {
      clearTimeout(timer);

      timer = setTimeout(
        () => fn.apply(this, args),
        wait
      );
    };
  }

  getQuery() {
    return this.input.value.trim();
  }

  onFormSubmit(event) {
    if (!this.getQuery()) {
      event.preventDefault();
    }
  }

  onFocus() {
    this.updateClearButton();

    if (!this.getQuery()) {
      this.showDefault();
      this.open();
      return;
    }

    if (
      this.getAttribute('results') === 'true'
    ) {
      this.showResults();
      this.open();
    } else {
      this.getSearchResults(
        this.getQuery()
      );
    }
  }

  onChange() {
    const searchTerm =
      this.getQuery();

    this.updateClearButton();

    if (!searchTerm) {
      if (this.abortController) {
        this.abortController.abort();
      }

      this.results.innerHTML = '';

      this.removeAttribute(
        'results'
      );

      this.showDefault();
      this.open();

      return;
    }

    this.showResults();
    this.open();

    this.getSearchResults(
      searchTerm
    );
  }

  getSearchResults(searchTerm) {
    const cacheKey =
      searchTerm
        .replace(/\s+/g, '-')
        .toLowerCase();

    if (
      this.cachedResults[cacheKey]
    ) {
      this.renderSearchResults(
        this.cachedResults[cacheKey]
      );

      return;
    }

    if (
      this.abortController
    ) {
      this.abortController.abort();
    }

    this.abortController =
      new AbortController();

    this.setLoadingState();

    const url =
      new URL(
        this.predictiveUrl,
        window.location.origin
      );

    url.searchParams.set(
      'q',
      searchTerm
    );

    url.searchParams.set(
      'section_id',
      'predictive-search'
    );

    url.searchParams.set(
      'resources[type]',
      'query,product,collection,article,page'
    );

    url.searchParams.set(
      'resources[limit]',
      '6'
    );

    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );

    url.searchParams.set(
      'resources[options][unavailable_products]',
      'last'
    );

    fetch(
      url.toString(),
      {
        signal:
          this.abortController.signal,

        headers: {
          Accept: 'text/html'
        }
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Predictive search failed: ${response.status}`
          );
        }

        return response.text();
      })
      .then((text) => {
        const responseDocument =
          new DOMParser()
            .parseFromString(
              text,
              'text/html'
            );

        const section =
          responseDocument
            .querySelector(
              '#shopify-section-predictive-search'
            );

        if (!section) {
          throw new Error(
            'Predictive search section was not returned.'
          );
        }

        const markup =
          section.innerHTML;

        this.cachedResults[
          cacheKey
        ] = markup;

        this.renderSearchResults(
          markup
        );
      })
      .catch((error) => {
        if (
          error.name === 'AbortError'
        ) {
          return;
        }

        console.error(error);

        this.results.innerHTML =
          '<p class="vss-native-search__empty">Search suggestions are temporarily unavailable. Press Enter to search.</p>';

        this.removeAttribute(
          'loading'
        );

        this.showResults();
        this.open();
      });
  }

  renderSearchResults(
    markup
  ) {
    this.results.innerHTML =
      markup;

    this.setAttribute(
      'results',
      'true'
    );

    this.removeAttribute(
      'loading'
    );

    const countValue =
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );

    if (countValue) {
      this.setLiveRegionText(
        countValue.textContent
      );
    }

    this.showResults();
    this.open();
  }

  setLoadingState() {
    this.setAttribute(
      'loading',
      'true'
    );

    this.setLiveRegionText(
      this.getAttribute(
        'data-loading-text'
      ) || 'Loading'
    );
  }

  setLiveRegionText(
    text
  ) {
    const status =
      this.querySelector(
        '.predictive-search-status'
      );

    if (!status) {
      return;
    }

    status.setAttribute(
      'aria-hidden',
      'false'
    );

    status.textContent =
      text;

    window.setTimeout(
      () => {
        status.setAttribute(
          'aria-hidden',
          'true'
        );
      },
      1000
    );
  }

  showDefault() {
    this.defaultState.hidden =
      false;

    this.results.hidden =
      true;
  }

  showResults() {
    this.defaultState.hidden =
      true;

    this.results.hidden =
      false;
  }

  updateClearButton() {
    const hasValue =
      this.input.value.trim().length > 0;

    if (this.clearButton) {
      this.clearButton.hidden =
        !hasValue;
    }

    if (this.exitButton) {
      this.exitButton.hidden =
        !hasValue;
    }
  }

  getHeaderBottom() {
    /*
      Measure the visible header row rather than the whole Shopify
      section. This stays accurate when the header is in its sticky
      state and prevents the search panel being positioned off-screen.
    */
    const visibleHeader =
      document.querySelector(
        '#shopify-section-header .header'
      );

    if (visibleHeader) {
      return Math.round(
        visibleHeader
          .getBoundingClientRect()
          .bottom
      );
    }

    return Math.round(
      this
        .getBoundingClientRect()
        .bottom
    );
  }

  positionLayers() {
    const headerBottom =
      this.getHeaderBottom();

    const viewportGap =
      16;

    const maxHeight =
      Math.max(
        320,
        window.innerHeight -
          headerBottom -
          viewportGap
      );

    /*
      Put these variables on <html>, not on the predictive-search
      custom element. The panel/backdrop are fixed viewport layers,
      so global variables are more reliable when the parent header
      changes into a sticky stacking context.
    */
    document.documentElement.style.setProperty(
      '--vss-search-top',
      `${headerBottom}px`
    );

    document.documentElement.style.setProperty(
      '--vss-search-max-height',
      `${maxHeight}px`
    );

    /*
      Also set the critical positioning directly. This makes the
      panel robust even if an older search stylesheet is cached or
      another rule wins the cascade.
    */
    if (this.panel) {
      this.panel.style.top =
        `${headerBottom}px`;

      this.panel.style.maxHeight =
        `${maxHeight}px`;
    }

    if (this.backdrop) {
      this.backdrop.style.top =
        `${headerBottom}px`;
    }
  }

  setHeaderControlsInteractive(
    enabled
  ) {
const controls = [
  document.querySelector('.header__icons'),
  document.getElementById('nav-hamburger')
].filter(Boolean);

    controls.forEach(
      (element) => {
        if (enabled) {
          element.removeAttribute(
            'inert'
          );

          element.removeAttribute(
            'aria-hidden'
          );
        } else {
          element.setAttribute(
            'inert',
            ''
          );

          element.setAttribute(
            'aria-hidden',
            'true'
          );
        }
      }
    );
  }

  open() {
    document.body.classList.add(
      'vss-native-search-open'
    );

    /*
      Position immediately, reveal the layers, then measure once more
      on the next frame. The second pass covers the header-height
      change caused by hiding the primary navigation while search opens.
    */
    this.positionLayers();

    this.panel.hidden =
      false;

    if (
      this.backdrop
    ) {
      this.backdrop.hidden =
        false;
    }

    this.input.setAttribute(
      'aria-expanded',
      'true'
    );

    this.setHeaderControlsInteractive(
      false
    );

    this.isOpen =
      true;

    requestAnimationFrame(
      () => {
        this.positionLayers();
      }
    );
  }

  close() {
    document.body.classList.remove(
      'vss-native-search-open'
    );

    this.panel.hidden =
      true;

    if (
      this.backdrop
    ) {
      this.backdrop.hidden =
        true;
    }

    this.input.setAttribute(
      'aria-expanded',
      'false'
    );

    this.removeAttribute(
      'loading'
    );

    this.setHeaderControlsInteractive(
      true
    );

    document.documentElement.style.removeProperty(
      '--vss-search-top'
    );

    document.documentElement.style.removeProperty(
      '--vss-search-max-height'
    );

    this.isOpen =
      false;
  }
}

if (
  !customElements.get(
    'predictive-search'
  )
) {
  customElements.define(
    'predictive-search',
    PredictiveSearch
  );
}